import { fetchBootstrap } from '@/api/public/bootstrap'
import { fetchCalendarEvents } from '@/api/public/events'
import {
	RESET_PUBLIC_CALENDAR,
	SET_PUBLIC_CALENDAR_BOOTSTRAP,
	SET_PUBLIC_CALENDAR_ERROR,
	SET_PUBLIC_CALENDAR_LOADING,
} from '@/store/mutation-types'

/**
 * Format a phone number as xxx-xxx-xxxx.
 *
 * @param {String} phone
 * @return {String|null}
 */
function formatOfficeNumber(phone) {
	if (!phone) {
		return null
	}

	const cleaned = ('' + phone).replace(/\D/g, '')
	const match = cleaned.match(/^(?:1)?(\d{3})(\d{3})(\d{4})$/)

	if (match) {
		return `${match[1]}-${match[2]}-${match[3]}`
	}

	return phone
}

/**
 * Normalize bootstrap API payload into store shape.
 *
 * @param {String} shortId
 * @param {Object} data
 * @return {Object}
 */
function normalizeBootstrapPayload(shortId, data) {
	return {
		calendars: data.calendars || [],
		linkedPartners: data.linked_partners || [],
		officeHoursStatus: data.office_hours_status,
		officeNumber: formatOfficeNumber(data.office_number),
		partnerName: data.partner_name,
		shortId,
		timezone: data.timezone,
	}
}

let inflightBootstrap = null
let inflightShortId = null

/**
 * Fetch bootstrap data from the API and update the store.
 *
 * @param {Object} context
 * @param {String} shortId
 * @return {Promise<void>}
 */
async function fetchAndStoreBootstrap({ commit, state }, shortId) {
	if (state.shortId && state.shortId !== shortId) {
		commit(RESET_PUBLIC_CALENDAR)
	}

	commit(SET_PUBLIC_CALENDAR_LOADING, true)
	commit(SET_PUBLIC_CALENDAR_ERROR, false)

	try {
		const data = await fetchBootstrap(shortId)

		commit(
			SET_PUBLIC_CALENDAR_BOOTSTRAP,
			normalizeBootstrapPayload(shortId, data)
		)
	} catch (error) {
		commit(SET_PUBLIC_CALENDAR_ERROR, true)
		throw error
	} finally {
		commit(SET_PUBLIC_CALENDAR_LOADING, false)
	}
}

export default {
	/**
	 * Load bootstrap data once per short id. Skips when already loaded.
	 * Used by route guards and partner short-id changes.
	 *
	 * @param {Object} context
	 * @param {String} shortId
	 * @return {Promise<void>}
	 */
	async ensureBootstrap({ dispatch, getters }, shortId) {
		if (!shortId) {
			return
		}

		if (getters.isBootstrapped && getters.shortId === shortId) {
			return
		}

		await dispatch('bootstrap', shortId)
	},

	/**
	 * Fetch bootstrap data from the API. Coalesces concurrent calls for the same short id.
	 *
	 * @param {Object} context
	 * @param {String} shortId
	 * @return {Promise<void>}
	 */
	async bootstrap(context, shortId) {
		if (!shortId) {
			return
		}

		if (inflightBootstrap && inflightShortId === shortId) {
			return inflightBootstrap
		}

		inflightShortId = shortId
		inflightBootstrap = fetchAndStoreBootstrap(context, shortId)

		try {
			await inflightBootstrap
		} finally {
			inflightBootstrap = null
			inflightShortId = null
		}
	},

	/**
	 * Re-fetch bootstrap data (e.g. polling). Always hits the API.
	 *
	 * @param {Object} context
	 * @param {String} shortId
	 * @return {Promise<void>}
	 */
	async refreshBootstrap({ dispatch }, shortId) {
		if (!shortId) {
			return
		}

		await dispatch('bootstrap', shortId)
	},

	async fetchEvents({ dispatch }, { calendarId, endsAt, startsAt }) {
		const parsedCalendarId = parseInt(calendarId, 10)

		if (Number.isNaN(parsedCalendarId) || !startsAt || !endsAt) {
			return
		}

		const events = await fetchCalendarEvents(
			parsedCalendarId,
			startsAt,
			endsAt
		)

		const eventList = Array.isArray(events) ? events : []

		await dispatch('calendars/removeEvents', null, { root: true })

		await dispatch(
			'calendars/add',
			[
				{
					id: parsedCalendarId,
					events: [],
					name: '',
					partner_id: 0,
				},
			],
			{ root: true }
		)

		if (!eventList.length) {
			return
		}

		const normalizedEvents = eventList.map(event => ({
			...event,
			calendar_id: event.calendar_id ?? parsedCalendarId,
		}))

		await dispatch('events/add', normalizedEvents, { root: true })

		await dispatch(
			'calendars/addEvents',
			{
				calendarId: parsedCalendarId,
				events: normalizedEvents.map(event => event.id),
			},
			{ root: true }
		)
	},
}
